import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';
import * as PropTypes from 'prop-types';
import AppContext from '../common/containers/AppContext';
import Loading from './components/Loading';
import getPodNames from './services/data/getPodNames';
import useGetRequest from '../common/services/useGetRequest';
import getCustomers from '../common/services/data/getCustomers';
import { CustomerStatus, FamilyStatus } from './types';
import getFamilies from './services/data/getFamilies';
import getPermissionsToFamilies from './services/permissions/getPermissionsToFamilies';
import hasPermission from './services/permissions/hasPermission';
import getRecentlySearchedItems from './services/data/getRecentlySearchedItems';
import PageGroupType from './components/PageGroupType';
import AppStatsPageContent from './components/AppStatsPageContent';
import NoAccessPage from './views/NoAccessPage';
import { PinnedExtensionsProvider } from './views/tools/usePinnedExtensions';

AppStatsPageContent.propTypes = { children: PropTypes.node };
export default function AppStatsAuthenticatedContainer() {
    const [user, userIsLoading] = useGetRequest('/ui/session/userinfo');
    const [customers, setCustomers] = useState(null);
    const [families, setFamilies] = useState(null);
    const [recentlySearchedIdsByType, setRecentlySearchedIdsByType] = useState(null);
    const [dateRangeSelectedOption, setDateRangeSelectedOption] = useState('Daily');
    const [podNames, setPodNames] = useState(null);

    // Customer Flex options (for filtering)
    const [regions, setRegions] = useState(null);
    const [customerSizes, setCustomerSizes] = useState(null);
    const [industries, setIndustries] = useState(null);
    const [segments, setSegments] = useState(null);

    const [searchParams] = useSearchParams();

    const isRestricted = !user?.permission_sets?.some((item) => item.role === 'ReadAll');

    const checkIsFullWeek = (startDateStr, endDateStr) => {
        if (!startDateStr || !endDateStr) {
            return false;
        }

        const start = new Date(startDateStr);
        const end = new Date(endDateStr);

        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
            return false;
        }

        const isMondayStart = start.getUTCDay() === 1;
        const isSundayEnd = end.getUTCDay() === 0;
        const sixDaysInMs = 518400000;
        const actualDiff = end.getTime() - start.getTime();
        const isExactlyOneWeek = actualDiff === sixDaysInMs;

        return isMondayStart && isSundayEnd && isExactlyOneWeek;
    };

    const checkIsFullMonth = (startDateStr, endDateStr) => {
        if (!startDateStr || !endDateStr) {
            return false;
        }

        const start = new Date(startDateStr);
        const end = new Date(endDateStr);

        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
            return false;
        }

        const isFirstDay = start.getUTCDate() === 1;

        const nextDay = new Date(end);
        nextDay.setUTCDate(end.getUTCDate() + 1);
        const isLastDay = nextDay.getUTCDate() === 1;

        const isSameMonth =
            start.getUTCMonth() === end.getUTCMonth() &&
            start.getUTCFullYear() === end.getUTCFullYear();

        return isFirstDay && isLastDay && isSameMonth;
    };

    const checkIsFullQuarter = (startDateStr, endDateStr) => {
        if (!startDateStr || !endDateStr) {
            return false;
        }

        const start = new Date(startDateStr);
        const end = new Date(endDateStr);

        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
            return false;
        }

        const startMonth = start.getUTCMonth(); // 0-11
        const startDay = start.getUTCDate();
        const startYear = start.getUTCFullYear();

        const endMonth = end.getUTCMonth();
        const endYear = end.getUTCFullYear();

        const isValidStartMonth = [0, 3, 6, 9].includes(startMonth);
        const isFirstDay = startDay === 1;
        const isCorrectEndMonth = endMonth === startMonth + 2;
        const nextDay = new Date(end);
        nextDay.setUTCDate(end.getUTCDate() + 1);
        const isLastDay = nextDay.getUTCDate() === 1;
        const isSameYear = startYear === endYear;

        return isValidStartMonth && isFirstDay && isCorrectEndMonth && isLastDay && isSameYear;
    };

    useEffect(() => {
        if (searchParams.has('endDate') && searchParams.has('startDate')) {
            if (
                searchParams.get('endDate') !== searchParams.get('startDate') &&
                dateRangeSelectedOption === 'Daily'
            ) {
                setDateRangeSelectedOption('Custom');
            }
            if (
                searchParams.get('endDate') !== searchParams.get('startDate') &&
                dateRangeSelectedOption !== 'Custom'
            ) {
                const startDateStr = searchParams.get('startDate');
                const endDateStr = searchParams.get('endDate');
                const isFullWeek = checkIsFullWeek(startDateStr, endDateStr);
                const isFullMonth = checkIsFullMonth(startDateStr, endDateStr);
                const isFullQuarter = checkIsFullQuarter(startDateStr, endDateStr);
                if (isFullWeek) {
                    setDateRangeSelectedOption('Weekly');
                } else if (isFullMonth) {
                    setDateRangeSelectedOption('Monthly');
                } else if (isFullQuarter) {
                    setDateRangeSelectedOption('Quarterly');
                }
            }
        } else if (
            !searchParams.has('endDate') &&
            !searchParams.has('startDate') &&
            dateRangeSelectedOption !== 'Custom'
        ) {
            setDateRangeSelectedOption('Daily');
        }
    }, [searchParams, dateRangeSelectedOption, setDateRangeSelectedOption]);

    const renderPageContent = useCallback(
        (children) => <AppStatsPageContent>{children}</AppStatsPageContent>,
        [],
    );

    useEffect(() => {
        if (userIsLoading || !user) {
            return;
        }

        if (isRestricted) {
            setCustomers([]);
            setFamilies([]);
            setRecentlySearchedIdsByType({ platform: [], family: [], customer: [] });
            setPodNames([]);
            return;
        }
        (async () => {
            const [
                initialCustomers,
                initialFamilies,
                initialRecentlySearchedFamilyIds,
                initialRecentlySearchedCustomerIds,
                initialRecentlySearchedPlatformIds,
                initialPods,
            ] = await Promise.all([
                getCustomers({ approvedPendingOnly: true }),
                getFamilies({ status: FamilyStatus.ACTIVE }),
                getRecentlySearchedItems(PageGroupType.FAMILY),
                getRecentlySearchedItems(PageGroupType.CUSTOMER),
                getRecentlySearchedItems(PageGroupType.PLATFORM),
                getPodNames(),
            ]);

            const nonMergedCustomers = initialCustomers.filter(
                ({ status }) => status !== CustomerStatus.MERGED,
            );
            setCustomers(nonMergedCustomers);

            const regionList = new Set();
            const customerSizeList = new Set();
            const industryList = new Set();
            const segmentList = new Set();

            nonMergedCustomers.forEach((customer) => {
                if (customer.flex) {
                    customer.flex.region?.forEach((r) => {
                        regionList.add(r);
                    });
                    customer.flex.size?.forEach((s) => {
                        customerSizeList.add(s);
                    });
                    customer.flex.industry?.forEach((i) => {
                        industryList.add(i);
                    });
                    if (customer.flex?.customer_segment) {
                        segmentList.add(customer.flex.customer_segment);
                    }
                }
            });
            setRegions(regionList);
            setCustomerSizes(customerSizeList);
            setIndustries(industryList);
            setSegments(segmentList);

            setFamilies(initialFamilies);
            setRecentlySearchedIdsByType({
                platform: initialRecentlySearchedPlatformIds,
                family: initialRecentlySearchedFamilyIds,
                customer: initialRecentlySearchedCustomerIds,
            });
            setPodNames(initialPods);
        })();
    }, [user, userIsLoading, isRestricted]);

    const appContext = useMemo(() => {
        const permissionsToFamilies = getPermissionsToFamilies(user);
        return {
            renderPageContent,
            user,
            customers,
            setCustomers,
            families,
            setFamilies,
            recentlySearchedIdsByType,
            setRecentlySearchedIdsByType,
            dateRangeSelectedOption,
            setDateRangeSelectedOption,
            permissionsToFamilies,
            userHasPermission: (permission, family) =>
                hasPermission(permissionsToFamilies, permission, family),
            podNames,
            setPodNames,
            regions,
            setRegions,
            customerSizes,
            setCustomerSizes,
            industries,
            setIndustries,
            segments,
            setSegments,
        };
    }, [
        user,
        renderPageContent,
        customers,
        families,
        recentlySearchedIdsByType,
        dateRangeSelectedOption,
        podNames,
        regions,
        customerSizes,
        industries,
        segments,
    ]);

    if (userIsLoading || !customers || !families) {
        return <Loading />;
    }

    if (isRestricted) {
        return <NoAccessPage />;
    }

    // Outlet will serve any content that a nested <Route /> matches.
    return (
        <AppContext.Provider value={appContext}>
            <PinnedExtensionsProvider>
                <Outlet />
            </PinnedExtensionsProvider>
        </AppContext.Provider>
    );
}
